import { inject, Injectable, LOCALE_ID, NgZone } from '@angular/core';
import { HttpTransportType, HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { asyncScheduler, BehaviorSubject, catchError, combineLatest, concatMap, defer, delay, distinctUntilChanged, EMPTY, filter, from, fromEvent, map, merge, NEVER, Observable, of, retry, Subject, switchMap, take, takeUntil, tap, throttleTime, throwError, timeout, timer } from 'rxjs';
import { I_RequestMessage, I_WebsocketParams } from '../../../models/ws-message.interface';
import { UtilsService } from '../../../utils/utils.service';
import { AuthService } from '../../auth.service';
import { AlphanumericCounter } from '../idGenerator.service';
import { AlarmService } from "../../alarm.service";
import { ConfigService } from '../../../utils/config.service';
import { E_DisplayBehavior, DisplayBehaviorService } from '../../../utils/display-behavior.service';
import { C_DisplayData } from '../../../models/display/domain/display.model';
import { E_SocketOperation } from '../../../enums/socket/socket-operation.enum';
import { E_SocketMessageType } from '../../../enums/socket/socket-message-type.enum';
import { DisplaySocketProcessorService } from './display-socket-message.processor';
import { ObsUtilsService } from '../../../utils/obs-utils.service';
import { SmartRotativeLayoutsService } from 'src/app/modules/main/pages/standard/pages/home/customization/smart-home/smart-rotative-layoutst.service';
import { BackendLocaleMap, E_SupportedLocales } from 'src/app/core/enums/locales.enum';
import { C_Token } from 'src/app/core/models/auth/token';


@Injectable(
  // {
  // providedIn: 'platform'
  // }
)
export class DisplayWsService {

  private utils = inject(UtilsService);
  private obsUtils = inject(ObsUtilsService);
  private auth = inject(AuthService);
  private alarmService = inject(AlarmService);
  private displayBehaviorService = inject(DisplayBehaviorService);
  private layoutsService = inject(SmartRotativeLayoutsService);
  private cfg = inject(ConfigService);
  private locale = inject(LOCALE_ID);
  private processor = inject(DisplaySocketProcessorService);
  private ngZone = inject(NgZone);
  private destroy$ = new Subject<void>();

  // WS MANAGEMENT
  private isDisconnected = true;
  private isDisconnected$: BehaviorSubject<boolean> = new BehaviorSubject(true);
  private connection?: HubConnection;
  private alphanumericCounter = new AlphanumericCounter();
  public connectionState$ = new BehaviorSubject<HubConnectionState>(HubConnectionState.Disconnected);
  private wsUrl: string = this.cfg.get("wsUrl") + '/subscribe';
  private reconnectTrigger$ = new BehaviorSubject<void>(undefined);
  // QUEUES
  private requestQueue$ = new Subject<I_PendingRequest>();
  private messageStream$ = new Subject<{ type: E_SocketMessageType, message: any }>();
  private pendingRequestsSubject$: BehaviorSubject<I_PendingRequest[]> = new BehaviorSubject<I_PendingRequest[]>([]);
  private worker?: Worker;

  // CURRENT DISPLAY
  private display: C_DisplayData = new C_DisplayData();
  private currentDisplayBehavior: E_DisplayBehavior = E_DisplayBehavior.Default;

  constructor() {
    this.initWorker();
    this.initMainWorkflow();
    this.initRequestQueue();
    this.initMessageStream();
    this.initNetworkStatusListeners();
  }

  private initWorker() {
    if (typeof Worker !== 'undefined') {
      this.worker = new Worker(new URL('../socket-processor.worker.ts', import.meta.url));
      this.worker.onmessage = ({ data }) => {
        this.executeStateUpdate(data.type, data.payload);
      };
    }
  }

  executeStateUpdate(type: E_SocketMessageType, message: any) {
    if (!message) return;
    this.messageStream$.next({ type, message });
  }

  /**
   * FLUJO PRINCIPAL: Orquestación de Display + Conexión
   */
  private initMainWorkflow() {
    combineLatest([
      this.obsUtils.getCurrentDisplay(),
      this.displayBehaviorService.getDisplayBehavior(),
      this.reconnectTrigger$ // <--- Ahora reacciona a este trigger
    ]).pipe(
      throttleTime(25, asyncScheduler, { leading: false, trailing: true }),
      filter(([info]) => !!info),
      switchMap(([info, behavior]) => {
        this.display = info!;
        return this.reconnectWorkflow(info!, behavior);
      }),
      takeUntil(this.destroy$)
    ).subscribe();
  }
  private reconnectWorkflow(info: C_DisplayData, behavior: E_DisplayBehavior): Observable<any> {
    return defer(() => {
      // Forzamos la limpieza del objeto antes de cada intento de creación
      if (this.connection) {
        this.connection.stop().catch(() => { });
        this.connection = undefined;
      }
      return this.connectToSignalR();
    }).pipe(
      tap(() => {
        this.layoutsService.setupHospitalFromDisplayData(info);
      }),
      switchMap(() => {
        return behavior === E_DisplayBehavior.Demo
          ? this.startDemoHeartbeat()
          : NEVER;
      }),
      retry({
        delay: (error) => {
          // Log crítico para depuración en producción
          console.error("WS CONNECTION FAILURE. Retrying in 10s...", error);
          return timer(10000);
        }
      })
    );
  }

  /**
   * COLA DE PETICIONES (Anti-Stuttering)
   */
  private initRequestQueue() {
    this.requestQueue$.pipe(
      // Evitamos enviar si no estamos conectados, reintentando cuando el estado sea Connected
      concatMap(req => this.connectionState$.pipe(
        filter(state => state === HubConnectionState.Connected),
        take(1),
        switchMap(() => from(this.sendSocketMessage(req))),
        delay(0) // Pequeño respiro entre mensajes para no saturar el canal
      )),
      takeUntil(this.destroy$)
    ).subscribe();
  }


  /**
   * ENVÍO DE MENSAJES (Pure RxJS)
   */
  private sendSocketMessage(req: I_PendingRequest): Observable<void> {
    const reqMessage: I_RequestMessage = {
      idMessage: this.alphanumericCounter.getNextId(),
      operation: req.operation,
      data: req.params,
    };

    // Transformamos el 'send' en un observable para que concatMap sepa cuándo termina
    return from(this.connection?.send(req.route || 'subscribe', JSON.stringify(reqMessage)) || EMPTY).pipe(
      retry(2), // Si falla un envío puntual, reintentamos antes de tirar la toalla
      tap({
        error: (err) => console.error('Error sending WS message', err)
      })
    );
  }

  /**
   * CONFIGURACIÓN DEL STREAM DE ENTRADA
   */
  private initMessageStream() {
    this.ngZone.runOutsideAngular(() => {
      this.messageStream$.pipe(
        // bufferTime(1, 1),
        // filter(batch => batch.length > 0),
        // observeOn(asyncScheduler),
        takeUntil(this.destroy$)
      ).subscribe((item) => {
        // batch.forEach(item => {
        this.processor.process(item.type, item.message, this.display);
        // });
        this.ngZone.run(() => {
          this.isDisconnected$.next(false);
        });
      });
    });
  }


  /**
   * GESTIÓN DE SIGNALR
   */
  private connectToSignalR(): Observable<void> {
    return from(this.auth.getToken()).pipe(
      timeout(15000), // Si el servidor de identidad no responde en 15s, fallamos para reintentar
      switchMap((data: C_Token | null) => {
        // Limpieza estricta
        const stop$ = this.connection
          ? from(this.connection.stop()).pipe(catchError(() => of(null)))
          : of(null);
        return stop$.pipe(map(() => data));
      }),
      switchMap((data: C_Token | null) => {
        if (!data?.accessToken) {
          // IMPORTANTE: throwError asegura que el retry externo se dispare
          return throwError(() => new Error("No hay token de acceso"));
        }

        const url = `${this.wsUrl}?token=${data.accessToken}`;
        const conn = new HubConnectionBuilder()
          .withUrl(url, {
            transport: HttpTransportType.WebSockets,
            skipNegotiation: true
          })
          .withAutomaticReconnect({
            nextRetryDelayInMilliseconds: () => 5000 // Reintento interno de SignalR cada 5s
          })
          .build();

        this.connection = conn;

        // Se dispara en cuanto se pierde la conexión y empieza a reintentar
        this.connection.onreconnecting((error) => {
          this.ngZone.run(() => {
            console.warn("SignalR: Reconectando...", error);
            this.connectionState$.next(HubConnectionState.Reconnecting);
          });
        });

        // Se dispara cuando recupera la conexión tras un fallo
        this.connection.onreconnected((connectionId) => {
          this.ngZone.run(() => {
            console.log("SignalR: Reconectado", connectionId);
            this.connectionState$.next(HubConnectionState.Connected);
            this.sendInitialSubscribe();
          });
        });

        this.connection.onclose(() => {
          this.ngZone.run(() => {
            this.connectionState$.next(HubConnectionState.Disconnected);
          });
        });

        this.connection.on('ReceiveMessage', (type, data) => this.handleMessage(type, data));
        this.connection.onclose(() => this.connectionState$.next(HubConnectionState.Disconnected));

        // Intentar el arranque con timeout
        return from(this.connection.start()).pipe(
          timeout(20000), // Si el handshake de WS tarda más de 20s, abortamos y reintentamos
          tap(() => {
            this.connectionState$.next(HubConnectionState.Connected);
            this.sendInitialSubscribe();
          }),
          catchError(err => {
            this.connectionState$.next(HubConnectionState.Disconnected);
            // Retornamos el error para que el retry() en reconnectWorkflow lo capture
            return throwError(() => err);
          })
        );
      })
    );
  }

  private sendInitialSubscribe() {
    const params: I_WebsocketParams = {
      displayId: this.display.id,
      sectionInfo: true,
      recordingAlerts: false,
      treatments: true,
      appointments: false,
      diagnosis: false,
      userName: this.auth.getUser()?.userName ?? '',
      relay: false,
      locale: BackendLocaleMap[this.locale as E_SupportedLocales],
      isDemo: this.currentDisplayBehavior == E_DisplayBehavior.Demo,
      // token: (await this.auth.getToken())?.accessToken
    };
    this.requestData(E_SocketOperation.DISPLAY_SUBSCRIBE, params);
  }

  public requestData(operation: string, params: I_WebsocketParams, route: string = 'subscribe'): void {
    this.requestQueue$.next({ operation, params, route });
  }

  /**
   * MODO DEMO (Heartbeat reactivo)
   */
  private startDemoHeartbeat(): Observable<never> {
    return timer(0, 50000).pipe(
      tap(() => {
        this.requestData(E_SocketOperation.DEMO, {
          isDemo: true,
          displayId: this.display.id,
          userName: this.auth.getUser()?.userName ?? '',
        });
      }),
      takeUntil(this.destroy$),
      switchMap(() => EMPTY) // Mantiene el stream vivo sin emitir basura
    );
  }

  private handleMessage(type: E_SocketMessageType, data: any) {
    // FORZAR ESTADO: Si recibimos algo, estamos conectados.
    if (this.connectionState$.value !== HubConnectionState.Connected) {
      this.connectionState$.next(HubConnectionState.Connected);
    }
    // Si el worker está activo, delegamos el parseo y resolveReferences
    if (this.worker) {
      this.worker.postMessage({ type, rawData: data });
    } else {
      // Fallback si no hay worker (parseo básico)
      this.executeStateUpdate(type, this.utils.jsonParse(data));
    }
  }

  /**
   * Function that returns the observable of the state for the disconnection with the websocket.
   */
  public getIsDisconnected(): Observable<boolean> {
    return this.connectionState$.pipe(
      // Mapeamos: solo estamos "conectados" si el estado es explícitamente Connected
      map(state => state !== HubConnectionState.Connected),
      distinctUntilChanged(),
      switchMap(isDisconnected => {
        if (isDisconnected) {
          return timer(1000).pipe(map(() => true));
        }
        return of(false);
      }),
      distinctUntilChanged(),
      // Forzamos que la emisión entre en la zona de Angular para que OnPush se entere
      o => new Observable<boolean>(subscriber => {
        const sub = o.subscribe({
          next: val => this.ngZone.run(() => subscriber.next(val)),
          error: err => this.ngZone.run(() => subscriber.error(err)),
          complete: () => this.ngZone.run(() => subscriber.complete())
        });
        return () => sub.unsubscribe();
      })
    );
  }

  private initNetworkStatusListeners() {
    merge(
      fromEvent(window, 'online').pipe(map(() => true)),
      fromEvent(window, 'offline').pipe(map(() => false))
    ).pipe(
      takeUntil(this.destroy$)
    ).subscribe(isOnline => {
      // En ambos casos limpiamos la conexión actual para evitar estados inconsistentes
      this.reset();

      if (isOnline) {
        // Solo si volvemos a estar online, forzamos el trigger de reconexión
        this.reconnectTrigger$.next();
      } else {
        // Si estamos offline, nos aseguramos de que el estado sea Disconnected inmediatamente
        this.connectionState$.next(HubConnectionState.Disconnected);
      }
    });
  }
  // Method to cancel all pending requests
  clearPendingRequests(): void {
    this.pendingRequestsSubject$.next([]);
  }

  /**
   * Function that resets all service subscriptions
   */
  reset() {
    if (this.connection) {
      this.connection.stop().catch(() => { });
      this.connection = undefined;
    }
    this.connectionState$.next(HubConnectionState.Disconnected);
    this.pendingRequestsSubject$.next([]);
    this.isDisconnected = true;
    this.isDisconnected$.next(this.isDisconnected);
    this.display.resetSubs();
    this.alarmService.resetPlayBackInterval()
  }

  /**
   * Function that returns the observable of the state for the disconnection with the websocket.
   */
  public hubStatus(): BehaviorSubject<HubConnectionState> {
    return this.connectionState$;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.reset();
    this.connectionState$.complete();
    this.requestQueue$.complete();
    this.messageStream$.complete();
    this.pendingRequestsSubject$.complete();
    this.isDisconnected$.complete();
    this.worker?.terminate();
  }

}

interface I_PendingRequest {
  operation: string;
  params: I_WebsocketParams;
  route?: string;
}
